import { useState } from 'react'

import { resultant, ru } from '../../lib/physics'
import { Arrow, ArrowDefs, Choice, Slider, useFrames } from './common'

/**
 * Тележку тянут и толкают (урок 38): верхнюю тележку разгоняют двое, нижнюю — один с силой,
 * равной равнодействующей. Тележки едут вровень: одна сила R меняет движение так же, как две
 * силы вместе. Трения нет, масса тележек одинаковая.
 */

const МАССА = 20 // кг
const PX_PER_M = 60
const PX_PER_N = 2.5
const X0 = 70

export default function CartLab() {
  const [тянет, setТянет] = useState(40)
  const [толкает, setТолкает] = useState(20)
  const [против, setПротив] = useState(false) // второй толкает назад
  const [s, setS] = useState({ x: 0, v: 0, идёт: false })

  const f2 = против ? -толкает : толкает
  const R = resultant([тянет, f2])
  const a = R / МАССА

  useFrames((dt) => {
    if (!s.идёт) return
    setS((p) => {
      const v = p.v + a * dt
      const x = p.x + v * dt
      const вышла = x * PX_PER_M > 220 || x < -1
      return { x, v, идёт: !вышла }
    })
  })

  const тележка = (y: number, подпись: string, силы: { f: number; kind: 'heavy' | 'el' | 'band'; label: string }[]) => {
    const x = X0 + s.x * PX_PER_M
    return (
      <g>
        <rect x={x} y={y} width={60} height={26} rx={4} className="fill-body ink" strokeWidth={1.5} />
        <circle cx={x + 14} cy={y + 30} r={6} className="fill-muted" />
        <circle cx={x + 46} cy={y + 30} r={6} className="fill-muted" />
        {силы.map((с, i) => (
          <Arrow key={i} x1={с.f >= 0 ? x + 60 : x} y1={y + 8 + i * 10} x2={(с.f >= 0 ? x + 60 : x) + с.f * PX_PER_N} y2={y + 8 + i * 10} kind={с.kind} label={с.label} labelDy={-2} />
        ))}
        <text x={8} y={y + 18} className="lab-tick">{подпись}</text>
      </g>
    )
  }

  return (
    <div className="card">
      <svg className="lab-svg" viewBox="0 0 360 180" role="img" aria-label="Две тележки: двое и один">
        <ArrowDefs />
        <line x1={0} y1={66} x2={360} y2={66} className="ink-muted" />
        <line x1={0} y1={156} x2={360} y2={156} className="ink-muted" />
        {тележка(30, 'двое', [
          { f: тянет, kind: 'heavy', label: 'F₁' },
          { f: f2, kind: 'el', label: 'F₂' },
        ])}
        {тележка(120, 'один', [{ f: R, kind: 'band', label: 'R' }])}
      </svg>

      <div className="lab-readout">
        <span className="band">R = {ru(тянет, 0)} {против ? '−' : '+'} {ru(толкает, 0)} = {ru(R, 0)} Н</span>
        <span>скорость обеих: {ru(Math.abs(s.v), 2)} м/с</span>
      </div>

      <Slider label="F₁ — первый тянет вперёд" value={тянет} min={0} max={60} step={5} unit="Н" onChange={(v) => { setТянет(v); setS({ x: 0, v: 0, идёт: false }) }} />
      <Slider label="F₂ — второй" value={толкает} min={0} max={60} step={5} unit="Н" onChange={(v) => { setТолкает(v); setS({ x: 0, v: 0, идёт: false }) }} />
      <Choice
        options={[{ value: 0, label: 'толкает вперёд' }, { value: 1, label: 'тянет назад' }]}
        value={против ? 1 : 0}
        onChange={(v) => { setПротив(v === 1); setS({ x: 0, v: 0, идёт: false }) }}
      />
      <div className="row-wrap" style={{ marginTop: 12 }}>
        <button className="btn btn-primary" onClick={() => setS({ x: 0, v: 0, идёт: true })}>▶️ Пуск</button>
      </div>
      <p className="muted small">
        Тележки едут вровень: одна сила R делает с тележкой то же, что две силы вместе. Поэтому
        несколько сил по одной прямой можно заменить их равнодействующей. Тянут вперёд и назад
        поровну — R = 0, тележка не трогается.
      </p>
    </div>
  )
}
